export interface Feedback {
  valid: boolean
  message?: string
  id?: string
}

export interface ObjectValidatorsMap<T extends object> {
  [key: string]: (id: string, value: any) => Feedback // TODO: How to restrict keys to keyof T?
}

export interface FieldValidator {
  getFieldValidator(k: string): (id: string, value: any) => Feedback
}

export default interface Validator<T> extends FieldValidator {
  validateField(t: T, k: string): Feedback
  validateObject(t: T): Feedback
}

export class ObjectMapValidator<T extends { [key: string]: any }> implements Validator<T> {
  constructor(private objectValidators: ObjectValidatorsMap<T>) {}

  public validateField(t: T, k: string): Feedback {
    const validator = this.objectValidators[k]
    if (validator) {
      const valid = validator(k, t[k])
      return valid
    } else {
      return { valid: true }
    }
  }

  public getFieldValidator(k: string) {
    return this.objectValidators[k]
  }

  public validateObject(t: T): Feedback {
    const feedback = Object.keys(this.objectValidators).map(k => {
      return this.validateField(t, k)
    })
    return aggregateFeedback(feedback)
  }
}

export const aggregateFeedback = (feedback: Feedback[]): Feedback =>
  feedback.reduce((p, n) => (n.valid ? p : n), { valid: true })

export const minCharactersValidator = (
  minCharacters: number,
  message: string
) => (id: string, value: string) => {
  if (value.length > minCharacters) {
    return { valid: true }
  } else {
    return {
      valid: false,
      message,
      id
    }
  }
}

export const maxCharactersValidator = (
  maxCharacters: number,
  message: string
) => (id: string, value: string) => {
  if (value.length < maxCharacters) {
    return { valid: true }
  } else {
    return {
      valid: false,
      message,
      id
    }
  }
}

export const rangeCharactersValidator = (
  minCharacters: number,
  maxCharacters: number,
  message: string
) => (id: string, value: string) => {
  if (value && value.length > minCharacters && value.length < maxCharacters) {
    return { valid: true }
  } else {
    return {
      valid: false,
      message,
      id
    }
  }
}

export const regexValidator = (regex: RegExp, message: string) => (
  id: string,
  value: string
) => {
  if (regex.test(value) || value === "") {
    return { valid: true }
  } else {
    return {
      valid: false,
      message,
      id
    }
  }
}
